import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Modal, Select, Button, Slider, InputNumber, Switch,
  Space, Spin, Empty, message, Tag,
} from "antd";
import { ReloadOutlined, DeleteOutlined, EyeOutlined } from "@ant-design/icons";
import InfraredCamera from "../../../api/InfraredCamera";

// ─── 常數 ────────────────────────────────────────────────────────────────────
const PAIR_COLORS = [
  "#FF3B30", "#FFCC00", "#34C759", "#00C7FF",
  "#AF52DE", "#FF9500", "#FF2D95", "#5AC8FA",
];
const DOT_R = 7;
const PREVIEW_MAX_W = 640;   // 預覽運算解析度，越小越快

// 可見光 OSD 區塊（正規化）。必須與後端 ThermalMSX.OSD_VISIBLE 一致。
const OSD_VISIBLE = [
  [0.70, 0.00, 1.00, 0.10],
  [0.00, 0.88, 0.40, 1.00],
];

// ─── 幾何：解 8 參數透視變換（純 JS，與 Python 端數學一致）──────────────────
function solveLinear(A, b) {
  const n = A.length;
  const M = A.map((row, i) => [...row, b[i]]);
  for (let col = 0; col < n; col++) {
    let piv = col;
    for (let r = col + 1; r < n; r++) {
      if (Math.abs(M[r][col]) > Math.abs(M[piv][col])) piv = r;
    }
    if (Math.abs(M[piv][col]) < 1e-12) {
      throw new Error("點位共線或重複，無法求解");
    }
    [M[col], M[piv]] = [M[piv], M[col]];
    const pv = M[col][col];
    for (let j = col; j <= n; j++) M[col][j] /= pv;
    for (let r = 0; r < n; r++) {
      if (r === col) continue;
      const f = M[r][col];
      if (f) for (let j = col; j <= n; j++) M[r][j] -= f * M[col][j];
    }
  }
  return M.map((row) => row[n]);
}

/**
 * src: 熱圖像素座標 [[x,y],...]
 * dst: 可見光「縮放到熱圖尺寸後」的座標 [[x,y],...]
 * 回傳 8 係數，語意與 PIL Image.PERSPECTIVE 相同（輸出座標 → 輸入座標）
 */
function solveHomography(src, dst) {
  if (src.length !== dst.length || src.length < 4) {
    throw new Error("至少需要 4 組對應點");
  }
  const rows = [], rhs = [];
  for (let i = 0; i < src.length; i++) {
    const [sx, sy] = src[i];
    const [dx, dy] = dst[i];
    rows.push([sx, sy, 1, 0, 0, 0, -sx * dx, -sy * dx]); rhs.push(dx);
    rows.push([0, 0, 0, sx, sy, 1, -sx * dy, -sy * dy]); rhs.push(dy);
  }
  if (src.length === 4) return solveLinear(rows, rhs);

  // 5 組以上用最小平方（正規方程 AᵀA x = Aᵀb）
  const ata = [], atb = [];
  for (let i = 0; i < 8; i++) {
    const r = [];
    for (let j = 0; j < 8; j++) r.push(rows.reduce((s, row) => s + row[i] * row[j], 0));
    ata.push(r);
    atb.push(rows.reduce((s, row, k) => s + row[i] * rhs[k], 0));
  }
  return solveLinear(ata, atb);
}

function applyH(c, x, y) {
  const den = c[6] * x + c[7] * y + 1;
  const d = Math.abs(den) < 1e-12 ? 1e-12 : den;
  return [(c[0] * x + c[1] * y + c[2]) / d, (c[3] * x + c[4] * y + c[5]) / d];
}

/** 套用 dx/dy 平移，與 Python 端 apply_msx 的處理相同 */
function shiftCoeffs(c, dx, dy) {
  if (!dx && !dy) return c;
  const out = [...c];
  out[2] = c[0] * (-dx) + c[1] * (-dy) + c[2];
  out[5] = c[3] * (-dx) + c[4] * (-dy) + c[5];
  return out;
}

// ─── 元件 ────────────────────────────────────────────────────────────────────
export default function InfraredCalibrationModal({ open, onClose, machineID }) {
  const [loading, setLoading]   = useState(false);
  const [imgLoading, setImgLoading] = useState(false);
  const [saving, setSaving]     = useState(false);

  const [dates, setDates]       = useState([]);
  const [times, setTimes]       = useState([]);
  const [selDate, setSelDate]   = useState(null);
  const [selTime, setSelTime]   = useState(null);

  const [pairs, setPairs]       = useState([]);
  const [pending, setPending]   = useState(null);   // 已點熱圖，等待點可見光
  const [coeffs, setCoeffs]     = useState(null);
  const [opacity, setOpacity]   = useState(0.85);
  const [dx, setDx]             = useState(0);
  const [dy, setDy]             = useState(0);
  const [enabled, setEnabled]   = useState(true);
  const [preview, setPreview]   = useState(false);
  const [stats, setStats]       = useState(null);
  const [updatedAt, setUpdatedAt] = useState(null);

  const thrImgRef  = useRef(null);   // HTMLImageElement
  const visImgRef  = useRef(null);
  const thrCanvas  = useRef(null);
  const visCanvas  = useRef(null);
  const urlsRef    = useRef([]);     // 待釋放的 objectURL

  const revokeUrls = () => {
    urlsRef.current.forEach((u) => URL.revokeObjectURL(u));
    urlsRef.current = [];
  };

  // ── 開啟時載入既有標定與日期清單 ─────────────────────────────────────────
  useEffect(() => {
    if (!open || !machineID) return;
    let cancelled = false;

    (async () => {
      setLoading(true);
      try {
        const [calRes, snapRes] = await Promise.all([
          InfraredCamera.GetCalibration(machineID),
          InfraredCamera.GetSnapshotDates(machineID),
        ]);
        if (cancelled) return;

        if (calRes.code === 200 && calRes.data) {
          const d = calRes.data;
          if (d.coeffs) setCoeffs(d.coeffs);
          if (Array.isArray(d.points)) {
            setPairs(d.points.map((p) => ({
              thermal: p.thermal, visible: p.visible,
            })));
          }
          setOpacity(d.opacity ?? 0.85);
          setDx(d.dx ?? 0);
          setDy(d.dy ?? 0);
          setEnabled(!!d.enabled);
          setUpdatedAt(d.updated_at || null);
        }

        if (snapRes.code === 200) {
          const ds = snapRes.data.dates || [];
          setDates(ds);
          if (ds.length) setSelDate(ds[0]);
          else message.warning("這台相機還沒有可用的 ch01/ch02 配對快照");
        }
      } catch {
        if (!cancelled) message.error("載入標定資料失敗");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [open, machineID]);

  // ── 關閉時清理 ───────────────────────────────────────────────────────────
  useEffect(() => {
    if (open) return;
    revokeUrls();
    thrImgRef.current = null;
    visImgRef.current = null;
    setPending(null);
    setPreview(false);
    setStats(null);
  }, [open]);

  // ── 選日期 → 抓時間清單 ──────────────────────────────────────────────────
  useEffect(() => {
    if (!open || !selDate) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await InfraredCamera.GetSnapshotTimes(machineID, selDate);
        if (cancelled || res.code !== 200) return;
        const ts = res.data.times || [];
        setTimes(ts);
        setSelTime(ts.length ? ts[0] : null);
      } catch {
        if (!cancelled) message.error("載入時間清單失敗");
      }
    })();
    return () => { cancelled = true; };
  }, [open, selDate, machineID]);

  // ── 選時間 → 載入兩張圖 ──────────────────────────────────────────────────
  useEffect(() => {
    if (!open || !selDate || !selTime) return;
    let cancelled = false;

    (async () => {
      setImgLoading(true);
      setPreview(false);
      try {
        const [thrBlob, visBlob] = await Promise.all([
          InfraredCamera.GetSnapshotBlob(machineID, "ch02", selDate, selTime),
          InfraredCamera.GetSnapshotBlob(machineID, "ch01", selDate, selTime),
        ]);
        if (cancelled) return;

        revokeUrls();
        const load = (blob) => new Promise((resolve, reject) => {
          const url = URL.createObjectURL(blob);
          urlsRef.current.push(url);
          const im = new Image();
          im.onload = () => resolve(im);
          im.onerror = () => reject(new Error("圖片解碼失敗"));
          im.src = url;
        });

        const [ti, vi] = await Promise.all([load(thrBlob), load(visBlob)]);
        if (cancelled) return;
        thrImgRef.current = ti;
        visImgRef.current = vi;
        drawAll();
      } catch {
        if (!cancelled) message.error("載入快照圖片失敗");
      } finally {
        if (!cancelled) setImgLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [open, selDate, selTime, machineID]);

  // ── 畫布繪製 ─────────────────────────────────────────────────────────────
  const drawOne = useCallback((canvasRef, img, key, extraPt) => {
    const cv = canvasRef.current;
    if (!cv || !img) return;
    const w = 640;
    const h = Math.round((img.naturalHeight / img.naturalWidth) * w);
    cv.width = w; cv.height = h;
    const ctx = cv.getContext("2d");
    ctx.drawImage(img, 0, 0, w, h);

    const sx = w / img.naturalWidth;
    const sy = h / img.naturalHeight;

    const items = pairs.map((p, i) => [i, p[key]]);
    if (extraPt) items.push([pairs.length, extraPt]);

    items.forEach(([idx, pt]) => {
      if (!pt) return;
      const cx = pt[0] * sx, cy = pt[1] * sy;
      const color = PAIR_COLORS[idx % PAIR_COLORS.length];
      ctx.beginPath();
      ctx.arc(cx, cy, DOT_R, 0, Math.PI * 2);
      ctx.fillStyle = color;
      ctx.fill();
      ctx.lineWidth = 2;
      ctx.strokeStyle = "#fff";
      ctx.stroke();
      ctx.font = "bold 14px Consolas, monospace";
      ctx.fillStyle = color;
      ctx.strokeStyle = "rgba(0,0,0,0.7)";
      ctx.lineWidth = 3;
      ctx.strokeText(String(idx + 1), cx + DOT_R + 4, cy - DOT_R - 2);
      ctx.fillText(String(idx + 1), cx + DOT_R + 4, cy - DOT_R - 2);
    });
  }, [pairs]);

  const drawAll = useCallback(() => {
    drawOne(thrCanvas, thrImgRef.current, "thermal", pending);
    drawOne(visCanvas, visImgRef.current, "visible", null);
  }, [drawOne, pending]);

  useEffect(() => { if (!preview) drawAll(); }, [pairs, pending, preview, drawAll]);

  // ── 點選 ─────────────────────────────────────────────────────────────────
  const canvasToImage = (canvasRef, img, evt) => {
    const cv = canvasRef.current;
    if (!cv || !img) return null;
    const rect = cv.getBoundingClientRect();
    const x = (evt.clientX - rect.left) / rect.width * img.naturalWidth;
    const y = (evt.clientY - rect.top) / rect.height * img.naturalHeight;
    if (x < 0 || y < 0 || x >= img.naturalWidth || y >= img.naturalHeight) return null;
    return [Math.round(x), Math.round(y)];
  };

  const onClickThermal = (e) => {
    if (preview) { message.info("預覽模式中無法點選，請先關閉預覽"); return; }
    const pt = canvasToImage(thrCanvas, thrImgRef.current, e);
    if (pt) setPending(pt);
  };

  const onClickVisible = (e) => {
    if (preview) { message.info("預覽模式中無法點選，請先關閉預覽"); return; }
    if (!pending) { message.info("請先在左邊熱圖點一個特徵點"); return; }
    const pt = canvasToImage(visCanvas, visImgRef.current, e);
    if (!pt) return;
    setPairs((prev) => [...prev, { thermal: pending, visible: pt }]);
    setPending(null);
  };

  const removePair = (i) => {
    setPairs((prev) => prev.filter((_, k) => k !== i));
    setPreview(false);
  };

  const clearPairs = () => {
    setPairs([]);
    setPending(null);
    setPreview(false);
    setStats(null);
  };

  // ── 計算 ─────────────────────────────────────────────────────────────────
  const compute = () => {
    const ti = thrImgRef.current, vi = visImgRef.current;
    if (!ti || !vi) { message.warning("請先載入快照"); return null; }
    if (pairs.length < 4) { message.warning(`至少需要 4 組對應點，目前 ${pairs.length} 組`); return null; }

    const tw = ti.naturalWidth, th = ti.naturalHeight;
    // 後端會先把可見光縮放到熱圖尺寸再變換，故此處先把可見光座標換算到同一尺度
    const kx = tw / vi.naturalWidth, ky = th / vi.naturalHeight;
    const src = pairs.map((p) => p.thermal);
    const dst = pairs.map((p) => [p.visible[0] * kx, p.visible[1] * ky]);

    try {
      const c = solveHomography(src, dst);
      const errs = src.map((s, i) => {
        const [px, py] = applyH(c, s[0], s[1]);
        return Math.hypot(px - dst[i][0], py - dst[i][1]);
      });
      const rms = Math.sqrt(errs.reduce((a, b) => a + b * b, 0) / errs.length);
      const diag = Math.hypot(tw, th);
      setCoeffs(c);
      setStats({
        rms: rms.toFixed(1),
        max: Math.max(...errs).toFixed(1),
        pct: (rms / diag * 100).toFixed(2),
        n: pairs.length,
      });
      return c;
    } catch (err) {
      message.error(err.message || "計算失敗");
      return null;
    }
  };

  // ── 預覽合成（與後端 apply_msx 同一套數學）──────────────────────────────
  const renderPreview = (c) => {
    const ti = thrImgRef.current, vi = visImgRef.current;
    const cv = thrCanvas.current;
    if (!ti || !vi || !cv || !c) return;

    const tw = ti.naturalWidth, th = ti.naturalHeight;
    const pw = Math.min(PREVIEW_MAX_W, tw);
    const ph = Math.round(th * pw / tw);
    const s = pw / tw;

    cv.width = pw; cv.height = ph;
    const ctx = cv.getContext("2d");
    ctx.drawImage(ti, 0, 0, pw, ph);
    const tData = ctx.getImageData(0, 0, pw, ph);

    // 可見光先拉伸到熱圖比例（對應後端的 resize((tw, th))）
    const off = document.createElement("canvas");
    off.width = pw; off.height = ph;
    const octx = off.getContext("2d");
    octx.drawImage(vi, 0, 0, pw, ph);
    const vData = octx.getImageData(0, 0, pw, ph);

    const m = shiftCoeffs(c, dx, dy);
    const out = ctx.createImageData(pw, ph);
    const op = opacity;

    for (let py = 0; py < ph; py++) {
      for (let px = 0; px < pw; px++) {
        const o = (py * pw + px) * 4;
        const sxT = px / s, syT = py / s;
        const den = m[6] * sxT + m[7] * syT + 1;
        const d = Math.abs(den) < 1e-12 ? 1e-12 : den;
        const vx = (m[0] * sxT + m[1] * syT + m[2]) / d;
        const vy = (m[3] * sxT + m[4] * syT + m[5]) / d;

        let useThermal = vx < 0 || vy < 0 || vx >= tw || vy >= th;
        if (!useThermal) {
          const nx = vx / tw, ny = vy / th;
          for (let k = 0; k < OSD_VISIBLE.length; k++) {
            const r = OSD_VISIBLE[k];
            if (nx >= r[0] && nx < r[2] && ny >= r[1] && ny < r[3]) { useThermal = true; break; }
          }
        }

        if (useThermal) {
          out.data[o] = tData.data[o];
          out.data[o + 1] = tData.data[o + 1];
          out.data[o + 2] = tData.data[o + 2];
        } else {
          const vi2 = ((Math.round(vy * s) * pw) + Math.round(vx * s)) * 4;
          for (let k = 0; k < 3; k++) {
            out.data[o + k] = vData.data[vi2 + k] * (1 - op) + tData.data[o + k] * op;
          }
        }
        out.data[o + 3] = 255;
      }
    }
    ctx.putImageData(out, 0, 0);
  };

  const onCompute = () => {
    const c = compute();
    if (!c) return;
    setPreview(true);
    setTimeout(() => renderPreview(c), 0);
  };

  const togglePreview = () => {
    if (preview) { setPreview(false); return; }
    const c = coeffs || compute();
    if (!c) return;
    setPreview(true);
    setTimeout(() => renderPreview(c), 0);
  };

  // 濃度/微調變動時即時重畫預覽
  useEffect(() => {
    if (preview && coeffs) renderPreview(coeffs);
  }, [opacity, dx, dy]);

  // ── 儲存 ─────────────────────────────────────────────────────────────────
  const onSave = async () => {
    const c = coeffs || compute();
    if (!c) return;
    setSaving(true);
    try {
      const res = await InfraredCamera.SaveCalibration(machineID, {
        coeffs: c,
        points: pairs,
        opacity,
        dx, dy,
        enabled: enabled ? 1 : 0,
      });
      if (res.code === 200) {
        message.success("標定已儲存，下一封報警信即生效");
        setUpdatedAt(new Date().toLocaleString("zh-TW", { hour12: false }));
      } else {
        message.error(res.message || "儲存失敗");
      }
    } catch (e) {
      message.error("儲存失敗");
    } finally {
      setSaving(false);
    }
  };

  const fmtTime = (t) => t ? `${t.slice(0, 2)}:${t.slice(2, 4)}:${t.slice(4, 6)}` : "";
  const ready = !!(thrImgRef.current && visImgRef.current);

  return (
    <Modal
      title="MSX 對位標定"
      open={open}
      onCancel={onClose}
      width="92%"
      style={{ maxWidth: 1400, top: 24 }}
      bodyStyle={{ padding: 16, maxHeight: "78vh", overflowY: "auto" }}
      footer={[
        <span key="ts" style={{ float: "left", color: "#999", fontSize: 12, lineHeight: "32px" }}>
          {updatedAt ? `上次儲存：${updatedAt}` : "尚未標定"}
        </span>,
        <Button key="close" onClick={onClose}>關閉</Button>,
        <Button key="save" type="primary" loading={saving}
          disabled={!coeffs && pairs.length < 4} onClick={onSave}>
          儲存標定
        </Button>,
      ]}
    >
      {loading ? (
        <div style={{ textAlign: "center", padding: 60 }}><Spin tip="載入中..." /></div>
      ) : dates.length === 0 ? (
        <Empty description="尚無可用的 ch01/ch02 配對快照" />
      ) : (
        <>
          {/* 快照選擇 */}
          <Space wrap style={{ marginBottom: 12 }}>
            <span style={{ color: "#666" }}>快照：</span>
            <Select value={selDate} onChange={setSelDate} style={{ width: 150 }}
              options={dates.map((d) => ({ value: d, label: d }))} />
            <Select value={selTime} onChange={setSelTime} style={{ width: 130 }}
              options={times.map((t) => ({ value: t, label: fmtTime(t) }))}
              notFoundContent="該日無配對" />
            <Tag color="blue">{pairs.length} 組對應點</Tag>
            {pending && <Tag color="orange">已選熱圖，請點右邊可見光</Tag>}
          </Space>

          {/* 並排畫布：窄螢幕自動換行成上下 */}
          <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
            {[
              { label: preview ? "ch02 熱圖（預覽合成中）" : "ch02 熱圖　← 先點這裡",
                ref: thrCanvas, onClick: onClickThermal },
              { label: "ch01 可見光　← 再點對應位置",
                ref: visCanvas, onClick: onClickVisible },
            ].map((p) => (
              <div key={p.label} style={{ flex: "1 1 480px", minWidth: 300 }}>
                <div style={{ fontSize: 12, color: "#666", marginBottom: 4 }}>{p.label}</div>
                <canvas ref={p.ref} onClick={p.onClick}
                  style={{
                    width: "100%", display: "block", cursor: preview ? "default" : "crosshair",
                    border: "1px solid #ddd", borderRadius: 4, background: "#f5f5f5",
                  }} />
              </div>
            ))}
          </div>

          {imgLoading && (
            <div style={{ textAlign: "center", padding: 12 }}><Spin tip="載入圖片..." /></div>
          )}

          {/* 操作列 */}
          <div style={{ marginTop: 12, display: "flex", flexWrap: "wrap", gap: 16, alignItems: "flex-start" }}>
            <Space direction="vertical" size={4}>
              <Space>
                <Button type="primary" icon={<ReloadOutlined />}
                  disabled={!ready || pairs.length < 4} onClick={onCompute}>
                  計算並預覽
                </Button>
                <Button icon={<EyeOutlined />} disabled={!ready || !coeffs}
                  onClick={togglePreview}>
                  {preview ? "關閉預覽" : "顯示預覽"}
                </Button>
                <Button icon={<DeleteOutlined />} danger disabled={!pairs.length}
                  onClick={clearPairs}>清除全部</Button>
              </Space>
              {stats && (
                <div style={{ fontSize: 12, color: "#555" }}>
                  {stats.n} 組點　殘差 RMS <b>{stats.rms}px</b>（對角線 {stats.pct}%）　最大 {stats.max}px
                  <div style={{ color: "#999" }}>
                    低於 0.5% 算堪用；超過 1% 肉眼就看得出偏移
                  </div>
                </div>
              )}
            </Space>

            <Space direction="vertical" size={2} style={{ minWidth: 240 }}>
              <span style={{ fontSize: 12, color: "#666" }}>熱圖濃度 {opacity.toFixed(2)}</span>
              <Slider min={0.5} max={1} step={0.01} value={opacity} onChange={setOpacity}
                style={{ width: 220, margin: "0 0 4px" }} />
              <Space size={4}>
                <span style={{ fontSize: 12, color: "#666" }}>微調 X</span>
                <InputNumber size="small" value={dx} onChange={(v) => setDx(v || 0)}
                  min={-200} max={200} style={{ width: 72 }} />
                <span style={{ fontSize: 12, color: "#666" }}>Y</span>
                <InputNumber size="small" value={dy} onChange={(v) => setDy(v || 0)}
                  min={-200} max={200} style={{ width: 72 }} />
              </Space>
              <Space size={6} style={{ marginTop: 4 }}>
                <Switch size="small" checked={enabled} onChange={setEnabled} />
                <span style={{ fontSize: 12, color: "#666" }}>啟用 MSX</span>
              </Space>
            </Space>

            {/* 對應點清單 */}
            <div style={{ flex: "1 1 220px", maxHeight: 150, overflowY: "auto" }}>
              {pairs.length === 0 ? (
                <div style={{ fontSize: 12, color: "#999" }}>
                  尚未建立對應點。<br />
                  挑兩邊都認得出的位置：礙子頂端、鐵構角點、線夾。<br />
                  盡量分散到四個角落，不要擠在同一區。
                </div>
              ) : pairs.map((p, i) => (
                <div key={i} style={{ fontSize: 11, fontFamily: "Consolas, monospace",
                  display: "flex", alignItems: "center", gap: 6, padding: "1px 0" }}>
                  <span style={{ width: 10, height: 10, borderRadius: "50%",
                    background: PAIR_COLORS[i % PAIR_COLORS.length], display: "inline-block" }} />
                  <span>{i + 1}. T({p.thermal[0]},{p.thermal[1]}) V({p.visible[0]},{p.visible[1]})</span>
                  <DeleteOutlined style={{ color: "#ff4d4f", cursor: "pointer" }}
                    onClick={() => removePair(i)} />
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </Modal>
  );
}